import { createColumnHelper } from '@tanstack/react-table';
import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from 'src/hooks/useInventory';
import {
  BreadcrumbHeading,
  MultiSelect,
  PriceBandIcon,
  SearchField,
  Table,
  Upload,
} from '@weareopenr/component-library';
import InventoryImportModal from '../../components/InventoryImportModal';
import EmptyState from '../../components/EmptyState';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { InventoryItemType, INVENTORY_TYPE_LABEL } from 'src/types/inventory.types';
import { mapInventoryToTable, TInventoryRow } from './mapInventoryToTable/mapInventoryToTable';
import Burger from 'src/assets/images/Burger';
import styles from './Inventory.module.scss';
import { isProductSyncActive } from 'src/helpers/isProductSyncActive/isProductSyncActive';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import { useProductLibraryStatus } from 'src/hooks/useProductLibraryStatus/useProductLibraryStatus';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { Routes } from 'src/routes';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

export const syncActiveEmptyStateText =
  'Your inventory will be displayed on this page once you have successfully synced products into your product library';

const columnHelper = createColumnHelper<TInventoryRow>();

const columns = [
  columnHelper.accessor('code', { header: 'Code', size: 140 }),
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('type', {
    header: 'Type',
    size: 120,
    cell: (info) => INVENTORY_TYPE_LABEL[info.getValue()],
  }),
  columnHelper.accessor('updatedAt', { header: 'Updated at', size: 160 }),
];

export const Inventory = (): ReactElement => {
  const { data: externalSystems } = useExternalSystems();

  const [searchQuery, setSearchQuery] = useState<string>();
  const [selectedTypes, setSelectedTypes] = useState<InventoryItemType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const debouncedSearchQuery = useDebounce(searchQuery);
  const productType = selectedTypes.length > 0 ? selectedTypes : undefined;
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInventory(
    50,
    debouncedSearchQuery,
    productType,
  );

  const navigate = useNavigate();
  const isProductSyncEnabled = useMemo(() => !!getSyncEndpoint(externalSystems), [externalSystems]);
  const { isSyncInProgress } = useProductLibraryStatus({ enabled: isProductSyncEnabled });
  const { trackEvent } = useUserTracking();

  useEffect(() => {
    document.title = 'Inventory';
    trackEvent('page_viewed', { page: 'inventory' });
  }, []);

  const typeOptions: InventoryItemType[] = ['RECIPE', 'GOOD'];

  const typeFilterOptions = typeOptions.map((type, index) => ({
    id: type,
    label: INVENTORY_TYPE_LABEL[type],
    display_order: index,
  }));

  const inventoryItems = useMemo(() => {
    const flattenedItems = data?.pages.flatMap((page) => page.data);
    if (!flattenedItems || flattenedItems.length === 0) return [];

    return mapInventoryToTable(flattenedItems);
  }, [data]);

  const showModal = (): void => {
    setIsModalVisible(true);
  };

  const closeModal = (): void => {
    setIsModalVisible(false);
  };

  const navigateToInventoryItem = useCallback(
    (recipeId: string): void => navigate(`${Routes.INVENTORY}/${recipeId}`),
    [navigate],
  );

  const handleOnSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const isRowClickable = useCallback(
    (row: TInventoryRow) => () => navigateToInventoryItem(row.id),
    [navigateToInventoryItem],
  );

  const renderContent = (): ReactElement => {
    if (isLoading || isSyncInProgress) return <SkeletonContainer hiddenTitle />;

    if (inventoryItems && inventoryItems.length > 0) {
      return (
        <div className={styles['table-container']}>
          <Table
            className={styles['table-fill-height']}
            data={inventoryItems}
            columns={columns}
            isRowClickable={isRowClickable}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>
      );
    }

    return (
      <div className={styles.emptyStateContainer}>
        {isProductSyncActive(externalSystems) ? (
          <EmptyState
            emptySectionText={syncActiveEmptyStateText}
            onClick={() => {
              navigate(Routes.PRODUCT_LIBRARY);
            }}
            buttonText="Go to Product Library"
            type="import-recipes-csv-disabled"
            Icon={Burger}
          />
        ) : (
          <EmptyState
            emptySectionText="Add your inventory items"
            onClick={showModal}
            buttonText="Import"
            type="import-recipes-csv"
            Icon={Burger}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles['page-container']}>
      <InventoryImportModal closeModal={closeModal} open={isModalVisible} />
      <div className={styles['header-container']}>
        <BreadcrumbHeading
          breadcrumbs={[{ label: 'Inventory', path: Routes.INVENTORY }]}
          optionMenuItems={
            isProductSyncEnabled
              ? []
              : [
                  {
                    id: 'import-inventory-csv',
                    name: 'Import inventory',
                    onOptionClick: showModal,
                    icon: Upload,
                  },
                ]
          }
        />
      </div>
      <div className={styles['search-container']}>
        <SearchField placeholder="Search for an inventory item" onChange={handleOnSearchChange} isDisabled={false} />
        <MultiSelect
          Icon={PriceBandIcon}
          label="Type"
          options={typeFilterOptions}
          selected={selectedTypes}
          onChange={(ids: string[]) => setSelectedTypes(ids as InventoryItemType[])}
          showSelectAllFooter
        />
      </div>
      {renderContent()}
    </div>
  );
};
